import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// ===============================
// CONSTANTS & CONFIGURATION
// ===============================

const GAME_CONFIG = {
  STARTING_WALLET: 100,
  TAXI_PRICES: {
    good: 20,
    fair: 30,
    tourist: 40
  },
  ROOM_PRICES: {
    standard: 200,
    deluxe: 350,
    suite: 500
  },
  GEM_REWARDS: {
    quiz: 5,
    bargain: 2,
    exploration: 3
  },
  CANVAS: {
    DEFAULT_WIDTH: 1200,
    DEFAULT_HEIGHT: 800,
    ASPECT_RATIO: 800 / 1200
  },
  BREAKPOINTS: {
    mobile: 375,
    tablet: 768,
    desktop: 1024
  }
};

const COUNTRIES = {
  'USA': { coords: [37.0902, -95.7129], region: 'usa' },
  'United States': { coords: [37.0902, -95.7129], region: 'usa' },
  'America': { coords: [37.0902, -95.7129], region: 'usa' },
  'UK': { coords: [55.3781, -3.4360], region: 'europe' },
  'United Kingdom': { coords: [55.3781, -3.4360], region: 'europe' },
  'England': { coords: [52.3555, -1.1743], region: 'europe' },
  'France': { coords: [46.2276, 2.2137], region: 'europe' },
  'Germany': { coords: [51.1657, 10.4515], region: 'europe' },
  'Spain': { coords: [40.4637, -3.7492], region: 'europe' },
  'Italy': { coords: [41.8719, 12.5674], region: 'europe' },
  'Netherlands': { coords: [52.1326, 5.2913], region: 'europe' },
  'Belgium': { coords: [50.5039, 4.4699], region: 'europe' },
  'Switzerland': { coords: [46.8182, 8.2275], region: 'europe' },
  'UAE': { coords: [23.4241, 53.8478], region: 'middleeast' },
  'Saudi Arabia': { coords: [23.8859, 45.0792], region: 'middleeast' },
  'Qatar': { coords: [25.3548, 51.1839], region: 'middleeast' },
  'Dubai': { coords: [25.2048, 55.2708], region: 'middleeast' },
  'Turkey': { coords: [38.9637, 35.2433], region: 'middleeast' },
  'Egypt': { coords: [26.8206, 30.8025], region: 'africa' },
  'Nigeria': { coords: [9.0820, 8.6753], region: 'africa' },
  'South Africa': { coords: [-30.5595, 22.9375], region: 'africa' },
  'Kenya': { coords: [-0.0236, 37.9062], region: 'africa' },
  'Algeria': { coords: [28.0339, 1.6596], region: 'africa' },
  'Tunisia': { coords: [33.8869, 9.5375], region: 'africa' },
};

const MOROCCO_COORDS = [33.5731, -7.5898]; // Casablanca

// Character avatars with fallback to emojis
const CHARACTER_AVATARS = {
  'fatima': {
    emoji: '🧕',
    image: '/assets/morocco-journey/characters/fatima.png',
    name: 'Fatima'
  },
  'taxi-driver': {
    emoji: '🚕',
    image: '/assets/morocco-journey/characters/taxi-driver.png',
    name: 'Ahmed'
  },
  'hotel-receptionist': {
    emoji: '👨‍💼',
    image: '/assets/morocco-journey/characters/receptionist.png',
    name: 'Youssef'
  },
  'souk-merchant': {
    emoji: '👳',
    image: '/assets/morocco-journey/characters/merchant.png',
    name: 'Karim'
  }
};

// Background images for different locations
const LOCATION_BACKGROUNDS = {
  'airport': '/assets/morocco-journey/locations/airport.png',
  'city': '/assets/morocco-journey/locations/city.png',
  'hotel': '/assets/morocco-journey/locations/hotel.png',
  'souk': '/assets/morocco-journey/locations/souk.png',
  'restaurant': '/assets/morocco-journey/locations/restaurant.png',
  'default': '/assets/morocco-journey/locations/default-cityscape.png'
};

// ===============================
// CUSTOM HOOKS
// ===============================

/**
 * Hook for managing responsive canvas size
 */
const useResponsiveCanvas = () => {
  const [canvasSize, setCanvasSize] = useState({
    width: GAME_CONFIG.CANVAS.DEFAULT_WIDTH,
    height: GAME_CONFIG.CANVAS.DEFAULT_HEIGHT
  });

  useEffect(() => {
    const updateSize = () => {
      const maxWidth = Math.min(window.innerWidth - 40, GAME_CONFIG.CANVAS.DEFAULT_WIDTH);
      const height = maxWidth * GAME_CONFIG.CANVAS.ASPECT_RATIO;
      setCanvasSize({ width: maxWidth, height });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return canvasSize;
};

/**
 * Hook for managing game state with persistence
 */
const useGameState = (userToken) => {
  // App-shell cache: read entire progress synchronously so wallet/gems/inventory
  // render from frame 1 instead of GAME_CONFIG defaults. Backend silently refreshes.
  const _readMjCache = () => {
    try { const c = localStorage.getItem('mjGameProgressCache'); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  const _cachedProgress = _readMjCache() || {};

  const [gameState, setGameState] = useState(_cachedProgress.game_state || 'location-select');
  const [wallet, setWallet] = useState(_cachedProgress.wallet ?? GAME_CONFIG.STARTING_WALLET);
  const [gems, setGems] = useState(_cachedProgress.gems || 0);
  const [inventory, setInventory] = useState(_cachedProgress.inventory || []);
  const [userLocation, setUserLocation] = useState(_cachedProgress.user_location || null);
  const [userMood, setUserMood] = useState(_cachedProgress.user_mood || null);
  const [currentRoom, setCurrentRoom] = useState(_cachedProgress.current_room || null);
  const [nights, setNights] = useState(_cachedProgress.nights || 1);

  // Load saved game state on mount
  useEffect(() => {
    if (userToken) {
      loadGameState();
    }
  }, [userToken]);

  // Auto-save game state when it changes
  useEffect(() => {
    if (userToken && gameState !== 'location-select') {
      saveGameState();
    }
  }, [gameState, wallet, gems, inventory, userLocation, userMood, currentRoom, nights]);

  const loadGameState = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/morocco/game/progress`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.progress) {
          setGameState(data.progress.game_state || 'location-select');
          setWallet(data.progress.wallet || GAME_CONFIG.STARTING_WALLET);
          setGems(data.progress.gems || 0);
          setInventory(data.progress.inventory || []);
          setUserLocation(data.progress.user_location);
          setUserMood(data.progress.user_mood);
          setCurrentRoom(data.progress.current_room);
          setNights(data.progress.nights || 1);
          try { localStorage.setItem('mjGameProgressCache', JSON.stringify(data.progress)); } catch {}
        }
      }
    } catch (error) {
      console.error('Failed to load game state:', error);
    }
  };

  const saveGameState = async () => {
    const payload = {
      game_state: gameState,
      wallet,
      gems,
      inventory,
      user_location: userLocation,
      user_mood: userMood,
      current_room: currentRoom,
      nights
    };
    // Mirror to localStorage immediately so a Ctrl+R during save still renders correct state
    try { localStorage.setItem('mjGameProgressCache', JSON.stringify(payload)); } catch {}
    try {
      await fetch(`${process.env.REACT_APP_API}/morocco/game/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      console.error('Failed to save game state:', error);
    }
  };

  return {
    gameState, setGameState,
    wallet, setWallet,
    gems, setGems,
    inventory, setInventory,
    userLocation, setUserLocation,
    userMood, setUserMood,
    currentRoom, setCurrentRoom,
    nights, setNights,
    loadGameState,
    saveGameState
  };
};

/**
 * Hook for managing character dialogue
 */
const useCharacterDialogue = () => {
  const [currentCharacter, setCurrentCharacter] = useState(null);
  const [dialogue, setDialogue] = useState('');
  const [dialogueOptions, setDialogueOptions] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);

  const showDialogue = useCallback((character, text, options = []) => {
    setCurrentCharacter(character);
    setDialogue(text);
    setDialogueOptions(options);
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);
  }, []);

  const clearDialogue = useCallback(() => {
    setCurrentCharacter(null);
    setDialogue('');
    setDialogueOptions([]);
    setIsAnimating(false);
  }, []);

  return {
    currentCharacter,
    dialogue,
    dialogueOptions,
    isAnimating,
    showDialogue,
    clearDialogue
  };
};

// ===============================
// MAIN GAME COMPONENT
// ===============================

function MoroccoJourneyGame({ userToken }) {
  // Game state management
  const {
    gameState, setGameState,
    wallet, setWallet,
    gems, setGems,
    inventory, setInventory,
    userLocation, setUserLocation,
    userMood, setUserMood,
    currentRoom, setCurrentRoom,
    nights, setNights
  } = useGameState(userToken);

  // Character dialogue management
  const {
    currentCharacter,
    dialogue,
    dialogueOptions,
    isAnimating,
    showDialogue,
    clearDialogue
  } = useCharacterDialogue();

  // Responsive canvas size
  const canvasSize = useResponsiveCanvas();

  // Additional state
  const [countryInput, setCountryInput] = useState('');
  const [countryError, setCountryError] = useState('');
  const [taxiPrice, setTaxiPrice] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [quizQuestion, setQuizQuestion] = useState(null);
  const [quizAnswered, setQuizAnswered] = useState(false);
  const [showInventory, setShowInventory] = useState(false);
  const [showMobileControls, setShowMobileControls] = useState(false);

  // Detect mobile device
  const isMobile = useMemo(() => {
    return window.innerWidth < GAME_CONFIG.BREAKPOINTS.tablet;
  }, []);

  // Handle country selection
  const handleCountrySubmit = useCallback(() => {
    const country = countryInput.trim();
    const countryData = COUNTRIES[country] || COUNTRIES[country.toLowerCase()];
    
    if (countryData) {
      setUserLocation({ name: country, ...countryData });
      setGameState('travel-confirm');
      setCountryError('');
    } else {
      setCountryError('Country not found. Try: USA, UK, France, Spain, Germany, UAE, Egypt, etc.');
    }
  }, [countryInput, setUserLocation, setGameState]);

  // Start journey
  const startJourney = useCallback(() => {
    setGameState('flying');
  }, [setGameState]);

  // Handle greeting response
  const handleGreetingResponse = useCallback((mood) => {
    setUserMood(mood);
    
    const responses = {
      good: "That's great! I'm so happy to hear that! 😊",
      tired: "Oh, I'm sorry to hear that. Don't worry, we'll make sure you get some rest soon.",
      happy: "Wonderful! Your positive energy makes me happy too! 🌟",
      hungry: "I understand! Moroccan cuisine is delicious. We'll get you some food soon!"
    };
    
    showDialogue('fatima', responses[mood] || "Welcome to Morocco!");
    
    setTimeout(() => {
      showMainMenu();
    }, 2000);
  }, [setUserMood, showDialogue]);

  // Show main menu
  const showMainMenu = useCallback(() => {
    setGameState('main-menu');
    showDialogue('fatima', 'What would you like to do first?', [
      { id: 'find-hotel', text: '🏨 Find Hotel', icon: '🏨' },
      { id: 'go-eat', text: '🍽️ Go Eat', icon: '🍽️' },
      { id: 'travel', text: '🚗 Travel', icon: '🚗' },
      { id: 'attractions', text: '🕌 Visit Attractions', icon: '🕌' },
      { id: 'relax', text: '🧘 Relax', icon: '🧘' },
      { id: 'explore', text: '🗺️ Explore', icon: '🗺️' },
      { id: 'souk', text: '🛍️ Visit Souk', icon: '🛍️' }
    ]);
  }, [setGameState, showDialogue]);

  // Handle main menu selection
  const handleMainMenuSelection = useCallback((option) => {
    setSelectedOption(option);
    
    switch (option.id) {
      case 'find-hotel':
        setGameState('find-hotel-intro');
        showDialogue('fatima', 'You will need to get a taxi. I have some prices for you!');
        setTimeout(() => showTaxiPrices(), 2000);
        break;
      case 'souk':
        setGameState('souk-intro');
        showDialogue('souk-merchant', 'Mar7ba! Welcome to the Souk! What would you like to buy or sell?');
        break;
      case 'go-eat':
        setGameState('restaurant-intro');
        showDialogue('fatima', 'Let me show you some great places to eat!');
        setTimeout(() => showMainMenu(), 2000);
        break;
      default:
        showDialogue('fatima', `The ${option.text} feature is coming soon in the full game!`);
        setTimeout(() => showMainMenu(), 2000);
    }
  }, [showDialogue, showMainMenu]);

  // Show taxi prices
  const showTaxiPrices = useCallback(() => {
    setGameState('taxi-prices');
    showDialogue('taxi-driver', 'Choose Your Price:', [
      { id: 'good-deal', text: `Good Deal: ${GAME_CONFIG.TAXI_PRICES.good} MAD ($${GAME_CONFIG.TAXI_PRICES.good / 10})`, price: GAME_CONFIG.TAXI_PRICES.good },
      { id: 'fair-price', text: `Fair Price: ${GAME_CONFIG.TAXI_PRICES.fair} MAD ($${GAME_CONFIG.TAXI_PRICES.fair / 10})`, price: GAME_CONFIG.TAXI_PRICES.fair },
      { id: 'tourist-price', text: `Tourist Price: ${GAME_CONFIG.TAXI_PRICES.tourist} MAD ($${GAME_CONFIG.TAXI_PRICES.tourist / 10})`, price: GAME_CONFIG.TAXI_PRICES.tourist }
    ]);
  }, [showDialogue]);

  // Handle price selection
  const handlePriceSelection = useCallback((option) => {
    setTaxiPrice(option.price);
    showDialogue('taxi-driver', `We are very happy to have you here in Morocco! The taxi will take you to the hotel for ${option.price} MAD.`);
    setTimeout(() => {
      setGameState('taxi-arriving');
      showDialogue('taxi-driver', 'Mar7ba! Welcome! Do you want to go to the hotel?', [
        { id: 'yes-agree', text: `Yes, for ${option.price} MAD`, price: option.price },
        { id: 'bargain-lower', text: `Can we do ${option.price - 5} MAD?`, price: option.price - 5 },
        { id: 'bargain-much-lower', text: `How about ${option.price - 10} MAD?`, price: option.price - 10 }
      ]);
    }, 2500);
  }, [showDialogue]);

  // Handle taxi bargain
  const handleTaxiBargain = useCallback((option) => {
    if (option.id === 'yes-agree') {
      showDialogue('taxi-driver', 'Perfect! Mar7ba! Let me take you to the hotel.');
      setTimeout(() => startTaxiDrive(), 2000);
    } else {
      const difference = taxiPrice - option.price;
      if (difference <= 5) {
        setTaxiPrice(option.price);
        setGems(gems + GAME_CONFIG.GEM_REWARDS.bargain);
        showDialogue('taxi-driver', `Okay, okay! ${option.price} MAD. You are a good negotiator! Mar7ba! (+${GAME_CONFIG.GEM_REWARDS.bargain} gems)`);
        setTimeout(() => startTaxiDrive(), 2000);
      } else {
        showDialogue('taxi-driver', `Ah no no, my friend! We agreed on ${taxiPrice} MAD. That's a good price already!`, [
          { id: 'yes-agree', text: `Okay, ${taxiPrice} MAD`, price: taxiPrice },
          { id: 'walk-away', text: 'I\'ll find another taxi', price: 0 }
        ]);
      }
    }
  }, [taxiPrice, gems, setGems, showDialogue]);

  // Start taxi drive
  const startTaxiDrive = useCallback(() => {
    setGameState('taxi-driving');
    clearDialogue();
    
    // After animation, show quiz if not answered
    setTimeout(() => {
      if (!quizAnswered) {
        showTaxiQuiz();
      } else {
        handleTaxiArrival();
      }
    }, 5000);
  }, [quizAnswered, clearDialogue, showTaxiQuiz, handleTaxiArrival]);

  // Show taxi quiz
  const showTaxiQuiz = useCallback(() => {
    setGameState('taxi-quiz');
    const questions = [
      {
        question: "How do you say 'thank you' in Darija?",
        options: ["Shukran", "Afak", "Mar7ba", "Salam"],
        correct: 0
      },
      {
        question: "What does 'Mar7ba' mean?",
        options: ["Goodbye", "Welcome", "Please", "Sorry"],
        correct: 1
      },
      {
        question: "How do you say 'yes' in Darija?",
        options: ["La", "Iyyeh", "Ma", "Kifach"],
        correct: 1
      }
    ];
    const randomQuestion = questions[Math.floor(Math.random() * questions.length)];
    setQuizQuestion(randomQuestion);
    showDialogue('taxi-driver', `${randomQuestion.question} (Answer correctly for ${GAME_CONFIG.GEM_REWARDS.quiz} gems!)`, randomQuestion.options.map((opt, idx) => ({
      id: idx,
      text: opt
    })));
  }, [showDialogue]);

  // Handle quiz answer
  const handleQuizAnswer = useCallback((option) => {
    if (quizAnswered) return;
    
    setQuizAnswered(true);
    const isCorrect = option.id === quizQuestion.correct;
    
    if (isCorrect) {
      setGems(gems + GAME_CONFIG.GEM_REWARDS.quiz);
      showDialogue('taxi-driver', `Correct! You earned ${GAME_CONFIG.GEM_REWARDS.quiz} gems! 🎉`);
    } else {
      showDialogue('taxi-driver', `Not quite. The answer is "${quizQuestion.options[quizQuestion.correct]}". Keep learning!`);
    }
    
    setTimeout(() => handleTaxiArrival(), 2000);
  }, [quizAnswered, quizQuestion, gems, setGems, showDialogue, handleTaxiArrival]);

  // Handle taxi arrival
  const handleTaxiArrival = useCallback(() => {
    setGameState('taxi-arrival');
    showDialogue('taxi-driver', `We have arrived! That will be ${taxiPrice} MAD please.`, [
      { id: 'pay', text: `Pay ${taxiPrice} MAD`, price: taxiPrice }
    ]);
  }, [taxiPrice, showDialogue]);

  // Handle payment
  const handlePayment = useCallback(() => {
    if (wallet >= taxiPrice) {
      setWallet(wallet - taxiPrice);
      showDialogue('taxi-driver', 'Shukran! Thank you! Welcome to Morocco! 🇲🇦');
      setTimeout(() => showHotelWelcome(), 2000);
    } else {
      showDialogue('taxi-driver', 'You don\'t have enough money!');
    }
  }, [wallet, taxiPrice, setWallet, showDialogue, showHotelWelcome]);

  // Show hotel welcome
  const showHotelWelcome = useCallback(() => {
    setGameState('hotel-welcome');
    showDialogue('hotel-receptionist', 'Merhba! Welcome to Hotel Casablanca! Do you have a reservation, or would you like to reserve a room?', [
      { id: 'have-reservation', text: 'I have a reservation', icon: '📋' },
      { id: 'want-reserve', text: 'I want to reserve a room', icon: '🔑' }
    ]);
  }, [showDialogue]);

  // Handle hotel option
  const handleHotelOption = useCallback((option) => {
    if (option.id === 'want-reserve') {
      setGameState('hotel-reservation');
      showDialogue('hotel-receptionist', 'Perfect! Here are our available rooms and prices:', [
        { id: 'standard', text: `Standard Room: ${GAME_CONFIG.ROOM_PRICES.standard} MAD/night`, price: GAME_CONFIG.ROOM_PRICES.standard },
        { id: 'deluxe', text: `Deluxe Room: ${GAME_CONFIG.ROOM_PRICES.deluxe} MAD/night`, price: GAME_CONFIG.ROOM_PRICES.deluxe },
        { id: 'suite', text: `Royal Suite: ${GAME_CONFIG.ROOM_PRICES.suite} MAD/night`, price: GAME_CONFIG.ROOM_PRICES.suite }
      ]);
    } else {
      showDialogue('hotel-receptionist', 'Great! Let me check your reservation...');
      setTimeout(() => showMainMenu(), 2000);
    }
  }, [showDialogue, showMainMenu]);

  // Handle room booking
  const handleRoomBooking = useCallback((option) => {
    const totalPrice = option.price * nights;
    if (wallet >= totalPrice) {
      setWallet(wallet - totalPrice);
      setCurrentRoom(option.id);
      showDialogue('hotel-receptionist', `Excellent! You've booked the ${option.text.replace(/ MAD\/night$/, '')} for ${nights} night(s). Total: ${totalPrice} MAD. Your room is ready!`);
      setTimeout(() => showMainMenu(), 3000);
    } else {
      showDialogue('hotel-receptionist', `I'm sorry, you don't have enough money. You need ${totalPrice} MAD but only have ${wallet} MAD.`);
    }
  }, [wallet, nights, setWallet, setCurrentRoom, showDialogue, showMainMenu]);

  // Handle inventory item action
  const handleInventoryAction = useCallback((itemId, action) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;

    if (action === 'sell') {
      const sellPrice = Math.floor(item.price * 0.7);
      setWallet(wallet + sellPrice);
      setInventory(inventory.filter(i => i.id !== itemId));
      showDialogue('souk-merchant', `Sold ${item.name} for ${sellPrice} MAD!`);
    } else if (action === 'use') {
      showDialogue('souk-merchant', `You used ${item.name}. ${item.effect || ''}`);
    }
  }, [inventory, wallet, setWallet, setInventory, showDialogue]);

  // Render game state
  return (
    <div style={styles.fullscreenContainer}>
      {/* Floating HUD */}
      <div style={styles.floatingHud}>
        <div style={styles.hudItem}>
          💰 {wallet} MAD
        </div>
        <div style={styles.hudItem}>
          💎 {gems} Gems
        </div>
        <button 
          onClick={() => setShowInventory(!showInventory)}
          style={styles.hudButton}
        >
          🎒 Inventory ({inventory.length})
        </button>
      </div>
      
      {/* Game title */}
      <div style={styles.gameTitle}>
        🇲🇦 Journey Through Morocco
      </div>

      {/* Inventory Panel */}
      {showInventory && (
        <div style={styles.inventoryPanel}>
          <div style={styles.inventoryHeader}>
            <h3>Your Inventory</h3>
            <button onClick={() => setShowInventory(false)} style={styles.closeButton}>✕</button>
          </div>
          <div style={styles.inventoryGrid}>
            {inventory.length === 0 ? (
              <p style={styles.emptyMessage}>Your inventory is empty. Visit the Souk to buy items!</p>
            ) : (
              inventory.map(item => (
                <div key={item.id} style={styles.inventoryItem}>
                  <div style={styles.itemIcon}>{item.icon}</div>
                  <div style={styles.itemInfo}>
                    <strong>{item.name}</strong>
                    <p>{item.description}</p>
                  </div>
                  <div style={styles.itemActions}>
                    <button 
                      onClick={() => handleInventoryAction(item.id, 'use')}
                      style={styles.actionButton}
                    >
                      Use
                    </button>
                    <button 
                      onClick={() => handleInventoryAction(item.id, 'sell')}
                      style={styles.sellButton}
                    >
                      Sell ({Math.floor(item.price * 0.7)} MAD)
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* LOCATION SELECTION SCREEN */}
      {gameState === 'location-select' && (
        <LocationSelectionScreen 
          countryInput={countryInput}
          setCountryInput={setCountryInput}
          onSubmit={handleCountrySubmit}
          error={countryError}
          isMobile={isMobile}
        />
      )}

      {/* TRAVEL CONFIRMATION */}
      {gameState === 'travel-confirm' && userLocation && (
        <div style={styles.confirmScreen}>
          <div style={styles.confirmBox}>
            <h2>🌍 Ready to Travel?</h2>
            <p>From: <strong>{userLocation.name}</strong></p>
            <p>To: <strong>Casablanca, Morocco 🇲🇦</strong></p>
            <button onClick={startJourney} style={styles.bigButton}>
              ✈️ Go to Morocco
            </button>
          </div>
        </div>
      )}

      {/* FLYING ANIMATION */}
      {gameState === 'flying' && (
        <FlyingScene 
          userLocation={userLocation} 
          onArrival={() => setGameState('arrival')} 
        />
      )}

      {/* ARRIVAL & CHARACTER GREETING */}
      {gameState === 'arrival' && (
        <CharacterScene
          character="fatima"
          dialogue="Merhba! Welcome to Morocco! 🇲🇦 How are you?"
          options={[
            { id: 'good', text: 'I am good', emoji: '😊' },
            { id: 'tired', text: 'I am tired', emoji: '😴' },
            { id: 'happy', text: 'I am happy', emoji: '😄' },
            { id: 'hungry', text: 'I am hungry', emoji: '😋' },
          ]}
          onSelect={handleGreetingResponse}
          backgroundImage={LOCATION_BACKGROUNDS.airport}
          isAnimating={isAnimating}
          isMobile={isMobile}
        />
      )}

      {/* CHARACTER DIALOGUE SCREENS */}
      {(gameState === 'main-menu' || gameState === 'find-hotel-intro' || 
        gameState === 'taxi-prices' || gameState === 'taxi-arriving' || 
        gameState === 'taxi-arrival' || gameState === 'hotel-welcome' || 
        gameState === 'hotel-reservation' || gameState === 'souk-intro' ||
        gameState === 'taxi-quiz') && (
        <CharacterScene
          character={currentCharacter}
          dialogue={dialogue}
          options={dialogueOptions}
          onSelect={(option) => {
            if (gameState === 'main-menu') handleMainMenuSelection(option);
            else if (gameState === 'taxi-prices') handlePriceSelection(option);
            else if (gameState === 'taxi-arriving') handleTaxiBargain(option);
            else if (gameState === 'taxi-arrival') handlePayment();
            else if (gameState === 'hotel-welcome') handleHotelOption(option);
            else if (gameState === 'hotel-reservation') handleRoomBooking(option);
            else if (gameState === 'taxi-quiz') handleQuizAnswer(option);
          }}
          backgroundImage={LOCATION_BACKGROUNDS.city}
          isAnimating={isAnimating}
          isMobile={isMobile}
        />
      )}

      {/* TAXI DRIVING SCENE */}
      {gameState === 'taxi-driving' && (
        <TaxiDrivingScene canvasSize={canvasSize} />
      )}

      {/* Mobile Controls */}
      {isMobile && dialogueOptions.length > 0 && (
        <div style={styles.mobileControls}>
          {dialogueOptions.map((option, idx) => (
            <button
              key={idx}
              onClick={() => {
                if (gameState === 'main-menu') handleMainMenuSelection(option);
                else if (gameState === 'taxi-prices') handlePriceSelection(option);
                else if (gameState === 'taxi-arriving') handleTaxiBargain(option);
                else if (gameState === 'taxi-arrival') handlePayment();
                else if (gameState === 'hotel-welcome') handleHotelOption(option);
                else if (gameState === 'hotel-reservation') handleRoomBooking(option);
                else if (gameState === 'taxi-quiz') handleQuizAnswer(option);
              }}
              style={styles.mobileButton}
            >
              {option.emoji || option.icon} {option.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ===============================
// SUB-COMPONENTS
// ===============================

function LocationSelectionScreen({ countryInput, setCountryInput, onSubmit, error, isMobile }) {
  return (
    <div style={styles.mapScreen}>
      <div style={styles.mapInstructions}>
        <h2>📍 Where are you traveling from?</h2>
        <p>Enter your country name:</p>
        
        <div style={styles.inputContainer}>
          <input 
            type="text"
            value={countryInput}
            onChange={(e) => setCountryInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && onSubmit()}
            placeholder="e.g., USA, UK, France, Germany, UAE..."
            style={isMobile ? styles.countryInputMobile : styles.countryInput}
          />
          <button onClick={onSubmit} style={styles.submitButton}>
            Search 🔍
          </button>
        </div>
        
        {error && <div style={styles.errorMessage}>{error}</div>}
        
        <div style={styles.exampleCountries}>
          <strong>Examples:</strong> USA, UK, France, Spain, Germany, Italy, UAE, Egypt, Turkey, Saudi Arabia
        </div>
      </div>
      
      <MapContainer 
        center={[40, 30]} 
        zoom={isMobile ? 2 : 2.5} 
        style={{ height: isMobile ? 'calc(100vh - 350px)' : 'calc(100vh - 300px)', width: '100%', borderRadius: '15px', flex: 1 }}
        scrollWheelZoom={false}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
          subdomains='abcd'
          maxZoom={19}
        />
        
        <Marker position={MOROCCO_COORDS}>
          <Popup>
            <strong>🇲🇦 Morocco</strong>
            <br />
            Your destination!
          </Popup>
        </Marker>
      </MapContainer>
    </div>
  );
}

function FlyingScene({ userLocation, onArrival }) {
  const mapRef = useRef(null);
  const [planePosition, setPlanePosition] = useState(userLocation.coords);
  const [progress, setProgress] = useState(0);
  const [mapCenter, setMapCenter] = useState(userLocation.coords);
  const [mapZoom, setMapZoom] = useState(4);
  const [showArrivalPopup, setShowArrivalPopup] = useState(false);
  const [flightPath, setFlightPath] = useState([]);
  
  useEffect(() => {
    let animationProgress = 0;
    let animationId = null;
    const startCoords = userLocation.coords;
    const endCoords = MOROCCO_COORDS;
    
    // Generate flight path for visualization
    const pathPoints = [];
    for (let i = 0; i <= 100; i++) {
      const t = i / 100;
      const lat = startCoords[0] + (endCoords[0] - startCoords[0]) * t;
      const lng = startCoords[1] + (endCoords[1] - startCoords[1]) * t;
      const curveLat = lat + Math.sin(t * Math.PI) * 5;
      pathPoints.push([curveLat, lng]);
    }
    setFlightPath(pathPoints);
    
    const animate = () => {
      if (animationProgress >= 1) {
        setProgress(100);
        setPlanePosition(endCoords);
        setMapCenter(endCoords);
        
        setTimeout(() => {
          setShowArrivalPopup(true);
        }, 500);
        return;
      }
      
      animationProgress += 0.003;
      const t = Math.min(animationProgress, 1);
      
      const lat = startCoords[0] + (endCoords[0] - startCoords[0]) * t;
      const lng = startCoords[1] + (endCoords[1] - startCoords[1]) * t;
      const curveLat = lat + Math.sin(t * Math.PI) * 5;
      
      setPlanePosition([curveLat, lng]);
      setMapCenter([curveLat, lng]);
      setProgress(Math.floor(t * 100));
      
      const zoomLevel = 4 + (t * 2);
      setMapZoom(zoomLevel);
      
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [userLocation, onArrival]);
  
  const airplaneIcon = L.divIcon({
    html: '<div style="font-size: 40px; transform: rotate(-45deg);">✈️</div>',
    className: 'airplane-marker',
    iconSize: [40, 40],
  });
  
  function MapUpdater() {
    const map = useMap();
    useEffect(() => {
      map.setView(mapCenter, mapZoom, { animate: true });
    }, [map, mapCenter, mapZoom]);
    return null;
  }
  
  return (
    <div style={styles.flyingMapContainer}>
      <div style={styles.flyingOverlay}>
        <h2 style={styles.flyingText}>✈️ Flying to Morocco...</h2>
        <div style={styles.progressBar}>
          <div style={{...styles.progressFill, width: `${progress}%`}}></div>
        </div>
        <p style={styles.progressText}>{progress}%</p>
      </div>
      
      {showArrivalPopup && (
        <div style={styles.arrivalPopupOverlay}>
          <div style={styles.arrivalPopup}>
            <h1 style={styles.arrivalTitle}>🇲🇦 Welcome to Morocco!</h1>
            <p style={styles.arrivalText}>
              Your journey to the beautiful Kingdom of Morocco is complete!
            </p>
            <p style={styles.arrivalText}>
              Are you ready to explore Casablanca?
            </p>
            <div style={styles.arrivalButtons}>
              <button onClick={() => onArrival()} style={styles.arrivalButton}>
                ✅ Keep Going
              </button>
              <button onClick={() => onArrival()} style={styles.arrivalButtonAlt}>
                🚪 Enter Morocco
              </button>
            </div>
          </div>
        </div>
      )}
      
      <MapContainer 
        center={userLocation.coords} 
        zoom={4}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
        zoomControl={false}
        dragging={false}
        ref={mapRef}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          attribution='&copy; OpenStreetMap &copy; CARTO'
          subdomains='abcd'
          maxZoom={19}
        />
        
        <MapUpdater />
        
        {flightPath.length > 0 && (
          <Polyline 
            positions={flightPath} 
            color="#FF6B6B" 
            weight={4} 
            opacity={0.7}
            dashArray="10, 10"
          />
        )}
        
        <Marker position={userLocation.coords}>
          <Popup>{userLocation.name}</Popup>
        </Marker>
        
        <Marker position={MOROCCO_COORDS}>
          <Popup>🇲🇦 Morocco</Popup>
        </Marker>
        
        <Marker position={planePosition} icon={airplaneIcon} />
      </MapContainer>
    </div>
  );
}

function CharacterScene({ character, dialogue, options, onSelect, backgroundImage, isAnimating, isMobile }) {
  const characterData = CHARACTER_AVATARS[character] || CHARACTER_AVATARS['fatima'];
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const handleImageLoad = () => setImageLoaded(true);
  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };
  
  return (
    <div style={{
      ...styles.characterScene,
      backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }}>
      <div style={styles.overlay}></div>
      
      <div style={styles.characterContainer}>
        <div style={styles.characterIcon}>
          {imageError || !characterData.image ? (
            <span style={styles.emojiFallback}>{characterData.emoji}</span>
          ) : (
            <img 
              src={characterData.image} 
              alt={characterData.name}
              style={styles.characterImage}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          )}
        </div>
        
        <div style={{
          ...styles.speechBubble,
          transform: isAnimating ? 'scale(1.05)' : 'scale(1)',
          transition: 'transform 0.3s ease'
        }}>
          <div style={styles.speechBubbleArrow}></div>
          <p style={styles.dialogueText}>{dialogue}</p>
          
          {options && options.length > 0 && !isMobile && (
            <div style={styles.optionsContainer}>
              {options.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => onSelect(option)}
                  style={styles.optionButton}
                >
                  {option.emoji || option.icon} {option.text}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TaxiDrivingScene({ canvasSize }) {
  const canvasRef = useRef(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  
  const POINT_A = { x: canvasSize.width * 0.8, y: canvasSize.height * 0.55, label: 'A - Airport' };
  const POINT_B = { x: canvasSize.width * 0.25, y: canvasSize.height * 0.55, label: 'B - Hotel' };
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let taxiX = POINT_A.x;
    
    // Draw background immediately (gradient sky)
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#87CEEB');    // Sky blue
    gradient.addColorStop(0.5, '#B0E0E6');  // Light blue
    gradient.addColorStop(1, '#FFFFFF');    // White
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw ground (green grass)
    ctx.fillStyle = '#4CAF50';
    ctx.fillRect(0, canvasSize.height * 0.7, canvas.width, canvasSize.height * 0.3);
    
    // Draw road (gray)
    ctx.fillStyle = '#607D8B';
    ctx.fillRect(0, canvasSize.height * 0.6, canvas.width, canvasSize.height * 0.15);
    
    // Load background image (optional)
    const img = new Image();
    img.src = LOCATION_BACKGROUNDS.city;
    img.onload = () => {
      setIsImageLoaded(true);
      animate();
    };
    img.onerror = () => {
      // Fallback to solid color if image fails to load
      setIsImageLoaded(true);
      animate();
    };
    
    const animate = () => {
      // Draw background
      if (img.complete) {
        ctx.drawImage(img, 0, 0, canvasSize.width, canvasSize.height);
      }
      
      // Draw route line
      ctx.strokeStyle = '#FF6B6B';
      ctx.lineWidth = 6;
      ctx.setLineDash([15, 10]);
      ctx.beginPath();
      ctx.moveTo(POINT_A.x, POINT_A.y);
      ctx.lineTo(POINT_B.x, POINT_B.y);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Draw location markers
      drawLocationMarker(ctx, POINT_A.x, POINT_A.y, 'A', '#FF0000');
      drawLocationMarker(ctx, POINT_B.x, POINT_B.y, 'B', '#00FF00');
      
      // Draw labels
      ctx.fillStyle = '#000';
      ctx.font = `bold ${isMobile ? 12 : 14}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText('Airport', POINT_A.x, POINT_A.y + 35);
      ctx.fillText('Hotel', POINT_B.x, POINT_B.y + 35);
      
      // Draw taxi
      drawTaxi(ctx, taxiX, POINT_A.y);
      
      // Move taxi towards Point B
      taxiX -= 5;
      
      if (taxiX > POINT_B.x) {
        requestAnimationFrame(animate);
      }
    };
  }, [canvasSize]);
  
  return (
    <div style={styles.fullScreenCanvas}>
      <canvas 
        ref={canvasRef} 
        width={canvasSize.width} 
        height={canvasSize.height}
        style={styles.canvas}
      />
      <div style={styles.drivingText}>
        🚕 Driving from Point A (Airport) to Point B (Hotel)...
      </div>
    </div>
  );
}

// Helper function to draw location markers
function drawLocationMarker(ctx, x, y, label, color) {
  ctx.fillStyle = color;
  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
  ctx.shadowBlur = 10;
  ctx.beginPath();
  ctx.arc(x, y, 25, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  
  ctx.fillStyle = '#FFFFFF';
  ctx.beginPath();
  ctx.arc(x, y, 18, 0, Math.PI * 2);
  ctx.fill();
  
  ctx.fillStyle = color;
  ctx.font = 'bold 24px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, x, y);
}

function drawTaxi(ctx, x, y) {
  // Shadow
  ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
  ctx.beginPath();
  ctx.ellipse(x, y + 20, 28, 10, 0, 0, Math.PI * 2);
  ctx.fill();
  
  // Body
  ctx.fillStyle = '#FFD700';
  ctx.fillRect(x - 30, y - 20, 60, 40);
  
  // Roof
  ctx.fillStyle = '#FF0000';
  ctx.fillRect(x - 18, y - 28, 36, 8);
  
  // Windows
  ctx.fillStyle = '#87CEEB';
  ctx.fillRect(x - 24, y - 15, 20, 18);
  ctx.fillRect(x + 4, y - 15, 20, 18);
  
  // Wheels
  ctx.fillStyle = '#333';
  ctx.beginPath();
  ctx.arc(x - 18, y + 20, 8, 0, Math.PI * 2);
  ctx.arc(x + 18, y + 20, 8, 0, Math.PI * 2);
  ctx.fill();
}

// ===============================
// STYLES
// ===============================

const styles = {
  fullscreenContainer: {
    width: '100%',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '0',
    fontFamily: 'Arial, sans-serif',
    position: 'relative',
  },
  floatingHud: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 2000,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    alignItems: 'flex-end',
  },
  hudItem: {
    fontSize: '18px',
    fontWeight: 'bold',
    background: 'rgba(0, 0, 0, 0.7)',
    color: '#FFD700',
    padding: '10px 20px',
    borderRadius: '20px',
    border: '2px solid #FFD700',
    boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
    textAlign: 'right',
  },
  hudButton: {
    fontSize: '16px',
    fontWeight: 'bold',
    background: 'rgba(0, 0, 0, 0.7)',
    color: '#FFD700',
    padding: '10px 20px',
    borderRadius: '20px',
    border: '2px solid #FFD700',
    boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
    cursor: 'pointer',
  },
  gameTitle: {
    position: 'fixed',
    top: '20px',
    left: '20px',
    zIndex: 2000,
    fontSize: '18px',
    fontWeight: 'bold',
    background: 'rgba(255, 255, 255, 0.9)',
    color: '#667eea',
    padding: '10px 20px',
    borderRadius: '15px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
  },
  inventoryPanel: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '80vh',
    background: 'white',
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    zIndex: 3000,
    overflow: 'hidden',
  },
  inventoryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '2px solid #f0f0f0',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
  },
  inventoryGrid: {
    padding: '20px',
    overflowY: 'auto',
    maxHeight: 'calc(80vh - 80px)',
  },
  emptyMessage: {
    textAlign: 'center',
    color: '#999',
    padding: '40px 20px',
  },
  inventoryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '15px',
    borderBottom: '1px solid #f0f0f0',
  },
  itemIcon: {
    fontSize: '40px',
    minWidth: '60px',
    textAlign: 'center',
  },
  itemInfo: {
    flex: 1,
  },
  itemActions: {
    display: 'flex',
    gap: '10px',
  },
  actionButton: {
    padding: '8px 16px',
    background: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  sellButton: {
    padding: '8px 16px',
    background: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  mapScreen: {
    background: 'white',
    minHeight: '100vh',
    padding: '40px',
    display: 'flex',
    flexDirection: 'column',
  },
  mapInstructions: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  inputContainer: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '20px',
    marginBottom: '15px',
    flexWrap: 'wrap',
  },
  countryInput: {
    padding: '15px 20px',
    fontSize: '18px',
    width: '400px',
    borderRadius: '10px',
    border: '2px solid #667eea',
    outline: 'none',
  },
  countryInputMobile: {
    padding: '12px 15px',
    fontSize: '16px',
    width: '100%',
    maxWidth: '300px',
    borderRadius: '10px',
    border: '2px solid #667eea',
    outline: 'none',
  },
  submitButton: {
    padding: '15px 30px',
    fontSize: '18px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  errorMessage: {
    color: '#D32F2F',
    fontSize: '16px',
    marginTop: '10px',
    fontWeight: 'bold',
  },
  exampleCountries: {
    marginTop: '15px',
    fontSize: '14px',
    color: '#666',
  },
  confirmScreen: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
  },
  confirmBox: {
    background: 'white',
    padding: '50px',
    borderRadius: '20px',
    textAlign: 'center',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
  },
  bigButton: {
    marginTop: '30px',
    padding: '20px 40px',
    fontSize: '24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '15px',
    cursor: 'pointer',
    fontWeight: 'bold',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
  },
  fullScreenCanvas: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100%',
    position: 'relative',
  },
  canvas: {
    border: '4px solid white',
    borderRadius: '15px',
    boxShadow: '0 10px 50px rgba(0,0,0,0.5)',
    maxWidth: '100%',
  },
  drivingText: {
    position: 'absolute',
    top: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '24px',
    color: 'white',
    fontWeight: 'bold',
    textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
    textAlign: 'center',
  },
  flyingMapContainer: {
    height: '100vh',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  flyingOverlay: {
    position: 'absolute',
    top: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    background: 'rgba(255, 255, 255, 0.95)',
    padding: '20px 40px',
    borderRadius: '15px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
  },
  flyingText: {
    margin: '0 0 15px 0',
    color: '#333',
  },
  progressBar: {
    width: '300px',
    height: '20px',
    background: '#E0E0E0',
    borderRadius: '10px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    transition: 'width 0.3s ease',
  },
  progressText: {
    marginTop: '10px',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#667eea',
  },
  arrivalPopupOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
    animation: 'fadeIn 0.5s ease-in',
  },
  arrivalPopup: {
    background: 'white',
    borderRadius: '25px',
    padding: '50px 60px',
    maxWidth: '600px',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    animation: 'slideUp 0.5s ease-out',
  },
  arrivalTitle: {
    fontSize: '42px',
    marginBottom: '20px',
    color: '#D32F2F',
    textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
  },
  arrivalText: {
    fontSize: '20px',
    color: '#333',
    marginBottom: '15px',
    lineHeight: '1.6',
  },
  arrivalButtons: {
    display: 'flex',
    gap: '20px',
    marginTop: '30px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  arrivalButton: {
    padding: '18px 40px',
    fontSize: '20px',
    background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '15px',
    cursor: 'pointer',
    fontWeight: 'bold',
    boxShadow: '0 6px 20px rgba(46, 125, 50, 0.4)',
    transition: 'transform 0.2s',
  },
  arrivalButtonAlt: {
    padding: '18px 40px',
    fontSize: '20px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '15px',
    cursor: 'pointer',
    fontWeight: 'bold',
    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
    transition: 'transform 0.2s',
  },
  characterScene: {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.4)',
  },
  characterContainer: {
    position: 'relative',
    zIndex: 10,
    display: 'flex',
    alignItems: 'flex-start',
    gap: '20px',
    maxWidth: '900px',
    padding: '20px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  characterIcon: {
    fontSize: '120px',
    background: 'white',
    borderRadius: '50%',
    width: '150px',
    height: '150px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
    border: '5px solid #FFD700',
    overflow: 'hidden',
  },
  characterImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  emojiFallback: {
    fontSize: '80px',
  },
  speechBubble: {
    background: 'white',
    borderRadius: '20px',
    padding: '25px',
    maxWidth: '600px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
    position: 'relative',
  },
  speechBubbleArrow: {
    position: 'absolute',
    left: '-20px',
    top: '40px',
    width: 0,
    height: 0,
    borderTop: '20px solid transparent',
    borderBottom: '20px solid transparent',
    borderRight: '20px solid white',
  },
  dialogueText: {
    fontSize: '20px',
    color: '#333',
    lineHeight: '1.6',
    marginBottom: '20px',
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  optionButton: {
    padding: '15px 20px',
    fontSize: '18px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'transform 0.2s, box-shadow 0.2s',
    textAlign: 'left',
  },
  mobileControls: {
    position: 'fixed',
    bottom: '20px',
    left: '0',
    right: '0',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '20px',
    zIndex: 2000,
  },
  mobileButton: {
    padding: '15px 20px',
    fontSize: '16px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: 'bold',
    width: '100%',
    textAlign: 'left',
  },
};

export default MoroccoJourneyGame;
